import { Permission } from '@server/permissions';
import { useSelector } from 'react-redux';
import { selectPermissionsByCrmOrgId } from '../../../reducers/userInfoReducer';
import { useCallback } from 'react';
import { telemetry } from '../../../telemetry';

export const usePermissions = () => {
  const permissionsByCrmOrgId = useSelector(selectPermissionsByCrmOrgId);

  /**
   * Checks each permission in the array for a given org.
   * @returns An array of booleans, where each boolean indicates if the corresponding permission is granted.
   */
  const checkPermissionsForOrg = useCallback(
    (permissionArray: Permission[], crmOrgId: string) => {
      if (!permissionsByCrmOrgId) {
        telemetry.captureError(new Error('Permissions by CRM org ID is not set'));
        return permissionArray.map(() => false);
      }
      const permissions = permissionsByCrmOrgId[crmOrgId];
      if (!permissions) {
        telemetry.captureError(new Error('Permissions for CRM org ID are not set'));
        return permissionArray.map(() => false);
      }
      return permissionArray.map((permission) => {
        return permissions.includes(permission);
      });
    },
    [permissionsByCrmOrgId],
  );

  /**
   * Checks if all permissions in the array are granted for a given org.
   * @returns `true` if all permissions are granted, `false` otherwise.
   */
  const checkAllPermissionsForOrg = useCallback(
    (permissionArray: Permission[], crmOrgId: string) => {
      return checkPermissionsForOrg(permissionArray, crmOrgId).every((permission) => permission);
    },
    [checkPermissionsForOrg],
  );

  /**
   * Checks if at least one permission in the array is granted for a given org.
   * @returns `true` if at least one permission is granted, `false` otherwise.
   */
  const checkAnyPermissionForOrg = useCallback(
    (permissionArray: Permission[], crmOrgId: string) => {
      return checkPermissionsForOrg(permissionArray, crmOrgId).some((permission) => permission);
    },
    [checkPermissionsForOrg],
  );

  return {
    checkPermissionsForOrg,
    checkAllPermissionsForOrg,
    checkAnyPermissionForOrg,
  };
};
